import { Box, Spacer } from '@eatclub-apps/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '../../../../../components/Button';
import { Card } from '../../../../../components/Card';
import NumberSelectRound from '../../../../../components/NumberSelect/NumberSelectRound';
import { experiencePropTypes, experiencesPropTypes } from '../../../../../data/models/Experience';
import { restaurantPropTypes } from '../../../../../data/models/Restaurant';
import { obeeAvailabilitySuggestionPropTypes } from '../../../../../data/models/obeeAvailabilitySuggestions';
import {
  formatCurrencyWithOptionalDecimals,
  formatIntFromTime,
  isEmpty,
  minutesToTimeString,
  safeDivision,
  timeStringToTime,
} from '../../../../../utils/helpers';
import { decodeHtmlSpecialCharacters, safeParse } from '../../../../../utils/stringHelpers';
import useStyles from './ExperienceCardStyles';

const ExperienceCard = ({
  experience,
  onClick,
  isSelected,
  guestAmount,
  onSelect,
  onDeselect,
  hasImages,
  count,
  experiences,
  obeeAvailabilitySuggestions,
  restaurant,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const [experienceAmount, setExperienceAmount] = useState(count || 0); // The amount of chosen selections of the experience

  // Obee backend team couldn't get accommodation working with the multiplier
  const isAccommodationOverride = experience?.experienceTypeSettings?.accommodation;

  // For accommodations, find the cheapest room price
  const getRoomPrice = () => {
    if (!isAccommodationOverride) {
      return 0;
    }

    const accommodationAddon = experience?.addonGroups?.[0]?.addons?.[0];

    const total = accommodationAddon?.price;

    const fullPrice =
      accommodationAddon?.percentageOfFullPrice < 100
        ? safeDivision(1, accommodationAddon?.percentageOfFullPrice) * total * 100
        : total;

    return fullPrice;
  };

  const roomPrice = getRoomPrice();

  // This sucks. Basically if something is "only dining" then we sort of pretend it's a special case and treat it with the same customisation as overrides
  const isDiningOverride =
    experience?.experienceTypeSettings?.dining &&
    !experience?.experienceTypeSettings?.menu &&
    !experience?.experienceTypeSettings?.event &&
    !experience?.experienceTypeSettings?.accommodation;

  const priceMultiplier = experience?.selectionMultiplyPrice ? guestAmount : 1;
  const isUsingSelections = !isAccommodationOverride && experience?.multiplier === 'per_person';

  const selectableTimes = experience?.selectableTimes
    ? {
        startTime: formatIntFromTime(
          timeStringToTime(experience?.selectableTimes?.startTime, 'h:mma'),
        ),
        endTime: formatIntFromTime(timeStringToTime(experience?.selectableTimes?.endTime, 'h:mma')),
        timeOption: experience?.selectableTimes?.timeOption,
        times: experience?.selectableTimes?.times?.map((time) =>
          formatIntFromTime(timeStringToTime(time, 'h:mma')),
        ),
      }
    : null;
  const requireExperienceSelection = experiences?.requireExperienceSelection;
  const hasMatchedSittingTimes = useMemo(() => {
    // When requireExperienceSelection is false, and there are available sitting times, check whether there is an overlapping time between experiences and available sitting times
    if (
      selectableTimes &&
      obeeAvailabilitySuggestions?.data?.length > 0 &&
      !requireExperienceSelection
    ) {
      return obeeAvailabilitySuggestions?.data?.some((sittingTime) => {
        if (selectableTimes?.timeOption === 'sitting_times') {
          return selectableTimes?.times?.some((t) => t === sittingTime.time);
        }
        return (
          sittingTime.time >= selectableTimes.startTime &&
          sittingTime.time <= selectableTimes.endTime
        );
      });
    }
    return true;
  }, [obeeAvailabilitySuggestions?.data, selectableTimes, requireExperienceSelection]);

  const isAvailable =
    experience.available > 0 &&
    !(isDiningOverride && experiences?.forceAccommodationSelection) &&
    hasMatchedSittingTimes;

  const handleChangeValue = (newValue) => {
    setExperienceAmount(newValue);
    if (newValue === 0) {
      onDeselect();
    } else {
      onSelect(newValue);
    }
  };

  const handleClick = (e) => {
    // if (!isAvailable) {
    //   return;
    // }

    // If using selections, we can click to select but not to deselect
    if (isUsingSelections) {
      if (!isSelected) {
        handleChangeValue(guestAmount);
        onClick(e);
      }
      return;
    }

    onClick(e);
  };

  // Reset the selected amount to 0 when deselected
  useEffect(() => {
    if (!isSelected) {
      setExperienceAmount(0);
    }
  }, [isSelected]);

  const generateAvailableTimesMessage = () => {
    if (selectableTimes.timeOption === 'all') {
      return null;
    }
    if (selectableTimes.timeOption === 'sitting_times') {
      return `Times: ${selectableTimes?.times
        ?.map((time) => minutesToTimeString(time))
        ?.join(', ')}`;
    }
    if (selectableTimes.timeOption === 'exactly') {
      return `Time: ${minutesToTimeString(selectableTimes?.startTime)}`;
    }
    if (selectableTimes.timeOption === 'after') {
      return `Time from: ${minutesToTimeString(selectableTimes?.startTime)}`;
    }
    if (selectableTimes.timeOption === 'before') {
      return `Time before: ${minutesToTimeString(selectableTimes?.endTime)}`;
    }
    // between case
    return `Times: ${minutesToTimeString(selectableTimes?.startTime)} - ${minutesToTimeString(
      selectableTimes?.endTime,
    )}`;
  };

  const showExperienceSelectableTimes = safeParse(restaurant?.data?.bookingWidgetSettings)
    ?.widgetSettings?.settingsDetails?.showExperienceSelectableTimes;

  const showSelectableTimes =
    showExperienceSelectableTimes == null ||
    restaurant.data?.showExperienceSelectableTimes === true;

  return (
    <Card
      className={classes.experience}
      key={experience.id}
      onClick={handleClick}
      active={isSelected}
      // disabled={!isAvailable}
      style={{
        wrapper: {
          backgroundColor: 'transparent',
          border: `1px solid ${theme.colors.outlineColour}`,
        },
      }}
    >
      <Box className={classes.experienceInner}>
        {hasImages ? (
          <Box className={classes.image}>
            {/* Replaced FittedImage with img to fix image stretching issues */}
            {!isEmpty(experience.imageUrl) && (
              <img
                src={experience.imageUrl}
                alt={`${experience.name}`}
                className={classes.experienceImage}
              />
            )}
          </Box>
        ) : (
          <></>
        )}

        <Spacer direction='horizontal' className={classes.content} gap={8}>
          <Spacer direction='vertical' className={classes.left} gap={16}>
            <Spacer direction='vertical' className={classes.top}>
              <Spacer direction='horizontal' className={classes.titleRow} gap={12}>
                <Box className={classes.title}>{experience.name}</Box>
                {isAvailable ? (
                  <Box className={classes.available}>
                    {experience.showAvailableNumber
                      ? `${experience.available} available`
                      : 'Available'}
                  </Box>
                ) : (
                  <Box className={classes.unavailable}>Unavailable</Box>
                )}
              </Spacer>

              {selectableTimes && showSelectableTimes && (
                <Box className={classes.description}>{generateAvailableTimesMessage()}</Box>
              )}

              <Box className={classes.description}>
                {decodeHtmlSpecialCharacters(experience.description)}
              </Box>
              {/* {roomPrice > 0 && ( */}
              {/*   <Box className={classes.description}> */}
              {/*     Additional rooms {formatCurrency(roomPrice)} per room */}
              {/*   </Box> */}
              {/* )} */}
            </Spacer>

            {isAvailable && (
              <>
                {!isUsingSelections && (
                  <Box className={classes.selectLabel}>{isSelected ? 'Selected' : 'Select'}</Box>
                )}
                {isUsingSelections && (
                  <Box className={classes.bottom}>
                    {!isSelected && (
                      <Button variant='text' onClick={() => handleChangeValue(guestAmount)}>
                        Select
                      </Button>
                    )}

                    {isSelected && (
                      <NumberSelectRound
                        onDecrease={() => handleChangeValue(experienceAmount - 1)}
                        onIncrease={() => handleChangeValue(experienceAmount + 1)}
                        min={0}
                        max={experience?.max || guestAmount}
                        value={experienceAmount}
                        onSetValue={handleChangeValue}
                        unit=''
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </Spacer>
          <Spacer direction='vertical' className={classes.right} gap={16}>
            {experience.selectionShowTotalPrice && (
              <>
                {roomPrice > 0 ? (
                  <Box className={classes.price}>
                    {experience.price > 0 &&
                      formatCurrencyWithOptionalDecimals(
                        priceMultiplier * experience.price + roomPrice,
                      )}
                  </Box>
                ) : (
                  <Box className={classes.price}>
                    {experience.price > 0 &&
                      formatCurrencyWithOptionalDecimals(priceMultiplier * experience.price)}
                  </Box>
                )}
              </>
            )}
          </Spacer>
        </Spacer>
      </Box>
    </Card>
  );
};

ExperienceCard.defaultProps = {
  count: 0,
};

const mapStateToProps = (state) => ({
  experiences: state.experiences,
  obeeAvailabilitySuggestions: state.obeeAvailabilitySuggestions,
  restaurant: state.restaurant,
});

ExperienceCard.propTypes = {
  experience: experiencePropTypes.isRequired,
  onClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  guestAmount: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDeselect: PropTypes.func.isRequired,
  hasImages: PropTypes.bool.isRequired,
  count: PropTypes.number,
  experiences: experiencesPropTypes.isRequired,
  obeeAvailabilitySuggestions: obeeAvailabilitySuggestionPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
};

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ExperienceCard);
